/**
 * Textos do onboarding e da janela "Sua primeira busca" (proposta "Onboarding" do Claude Design,
 * 17/10/2026). O português fica como padrão no código (chaves `ob_*` em src/i18n/locales); en,
 * es e fr vêm dos arquivos de idioma. Setores, segmentos e sugestões usam o português do
 * catálogo como padrão (`ob_sector_<id>`, `ob_seg_<id>`, `ob_sug_<id>_term|kw|why`).
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SUGGESTIONS, type SuggestionId } from './suggestions';
import type { Sector, Segment } from './catalog';

export const ONBOARDING_PT = {
  step_of: 'Passo {{n}} de {{total}}',

  complete_title: 'Complete seu perfil',
  complete_text: 'Para sugerir buscas mais certeiras, conte um pouco mais sobre o seu negócio.',

  sector_title: 'O que a sua empresa faz?',

  segment_title: 'Qual é o seu segmento?',
  other_which: 'Outro, qual?',
  other_label: 'Qual é o seu segmento?',
  other_placeholder: 'Escreva o seu segmento',

  customer_title: 'Para quem você vende?',
  c_empresas: 'Empresas',
  c_empresas_text: 'Clínicas, lojas, academias, escritórios',
  c_profissionais: 'Profissionais',
  c_profissionais_text: 'Médicos, advogados, corretores, fotógrafos',
  c_pessoas: 'Pessoas físicas',
  c_pessoas_text: 'Estudantes, aposentados, consumidores',
  c_todos: 'Todos',
  c_todos_text: 'Empresas, profissionais e pessoas',

  reach_title: 'Onde estão seus clientes?',
  r_cidade: 'Cidade',
  r_cidade_text: 'Meus clientes ficam na mesma cidade que eu.',
  r_regiao: 'Região',
  r_regiao_text: 'Na minha cidade e nas cidades vizinhas.',
  r_estado: 'Estado',
  r_estado_text: 'Em qualquer cidade do meu estado.',
  r_pais: 'Todo o país',
  r_pais_text: 'Vendo para o país inteiro.',
  r_online: 'Em qualquer lugar / online',
  r_online_text: 'Atendo à distância, sem depender de onde o cliente está.',

  origin_title: 'Como você conheceu a {{product}}?',
  origin_text: 'Escolha uma opção. Isso nos ajuda a chegar em mais gente como você.',
  g_social: 'Busca e redes sociais',
  g_ai: 'Assistentes de IA',
  g_people: 'Pessoas, eventos e conteúdo',
  o_indicacao: 'Indicação',
  o_grupos: 'Grupos',
  o_evento: 'Evento',
  o_curso: 'Curso ou mentoria',
  o_podcast: 'Podcast',
  o_artigo: 'Artigo ou notícia',
  o_forum: 'Fórum',
  o_outros: 'Outros',
  d_indicacao: 'Quem indicou?',
  d_grupos: 'Em qual grupo?',
  d_evento: 'Qual evento?',
  d_curso: 'Qual curso ou mentoria?',
  d_podcast: 'Qual podcast?',
  d_artigo: 'Onde você leu?',
  d_forum: 'Qual fórum?',
  d_outros: 'Onde?',
  optional: 'Opcional',


  back: 'Voltar',
  continue: 'Continuar',
  finish: 'Concluir',
  saving: 'Salvando…',
  retry: 'Tentar de novo',
  save_error: 'Não conseguimos salvar suas respostas. Elas continuam aqui; tente de novo.',
  offline:
    'Você está sem conexão. Suas respostas continuam aqui; tente de novo quando a internet voltar.',
  val_sector: 'Escolha o setor para continuar.',
  val_segment: 'Escolha um segmento ou use “Outro, qual?”.',
  val_other: 'Escreva o seu segmento para continuar.',
  val_customer: 'Escolha para quem você vende.',
  val_reach: 'Escolha onde estão seus clientes.',
  val_origin: 'Escolha como você conheceu a ferramenta.',

  fs_title: 'Sua primeira busca',
  fs_text: 'Separamos 3 buscas para o seu perfil: {{profile}}. Escolha uma para começar.',
  fs_profile: '{{segment}} que vende para {{customers}} {{where}}',
  fs_your_business: 'Seu negócio',
  cw_empresas: 'empresas',
  cw_profissionais: 'profissionais',
  cw_pessoas: 'pessoas físicas',
  cw_todos: 'empresas, profissionais e pessoas',
  k_loc: 'Negócios locais',
  k_tel: 'Telefones',
  k_mail: 'Emails',
  recommended: 'Mais indicada',
  note_city: 'Usamos {{city}}, a cidade da sua conexão. Dá para trocar o lugar na tela da busca.',
  note_state:
    'Usamos o estado da sua conexão, {{state}}, começando por {{city}}. Dá para trocar ou somar cidades na tela da busca.',
  note_country:
    'Os negócios locais começam por {{city}}, a cidade da sua conexão. Dá para somar outras cidades na tela da busca.',
  note_none: 'Você escolhe o lugar na tela da busca.',
  own_search: 'Prefiro montar minha busca',
  do_search: 'Fazer esta busca',
  close: 'Fechar',

  in_city: 'em {{city}}',
  in_region: 'em {{city}} e região',
  region: '{{city}} e região',
  in_state: 'em {{state}}',
  in_brazil: 'em todo o Brasil',
  in_country: 'em todo o país',
  brazil: 'todo o Brasil',
  country: 'todo o país',
  anywhere: 'em qualquer lugar',
  all_ddd: 'todos os DDDs',
  ddd: 'DDD {{ddd}}',
} as const;

export type OnboardingKey = keyof typeof ONBOARDING_PT;

export function useOnboardingText() {
  const { t, i18n } = useTranslation();
  const lang = (i18n?.language ?? 'pt').slice(0, 2);

  const tx = useCallback(
    (key: OnboardingKey, vars?: Record<string, string | number>): string =>
      t(`ob_${key}`, {
        defaultValue: ONBOARDING_PT[key],
        ...vars,
        interpolation: { escapeValue: false },
      }) as string,
    [t],
  );
  const sectorName = useCallback(
    (sector: Sector): string =>
      t(`ob_sector_${sector.id}`, { defaultValue: sector.name }) as string,
    [t],
  );
  const segmentName = useCallback(
    (segment: Segment): string =>
      t(`ob_seg_${segment.id}`, { defaultValue: segment.name }) as string,
    [t],
  );
  const suggestion = useCallback(
    (id: SuggestionId, field: 'term' | 'kw' | 'why'): string =>
      t(`ob_sug_${id}_${field}`, { defaultValue: SUGGESTIONS[id]?.[field] ?? '' }) as string,
    [t],
  );

  return { tx, sectorName, segmentName, suggestion, lang };
}
